// src/services/sanitaryWorkPatternService.js
// =============================================================================
// Frontend API wrapper for sanitary work patterns
// =============================================================================

import apiClient from "../utils/apiClient";

const DEBUG_API =
  String(process.env.REACT_APP_DEBUG_API || "").toLowerCase().trim() === "true";

function getErrorMessage(err) {
  return (
    err?.response?.data?.message ||
    err?.response?.data?.error ||
    err?.response?.data?.detail ||
    err?.message ||
    "Request failed"
  );
}

function throwApiError(context, err) {
  const message = getErrorMessage(err);
  const e = new Error(`${context}: ${message}`);
  e.status = err?.response?.status;
  e.code = err?.response?.data?.code || err?.code;
  e.data = err?.response?.data;

  if (DEBUG_API) {
    // eslint-disable-next-line no-console
    console.error(`[SanitaryWorkPatternService] ${context}`, {
      status: e.status,
      code: e.code,
      message,
      data: e.data,
    });
  }

  throw e;
}

function assertId(context, id) {
  if (id === undefined || id === null || `${id}`.trim() === "") {
    const e = new Error(`${context}: id is required.`);
    e.code = "ID_MISSING";
    throw e;
  }
}

function buildParams(params = {}) {
  return Object.fromEntries(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== null && value !== "")
  );
}

function cleanOptionalText(value) {
  if (value === undefined || value === null) return null;
  const s = String(value).trim();
  return s || null;
}

function normalizeDays(days = []) {
  return Array.from({ length: 7 }, (_, index) => {
    const dow = index + 1;
    const row = Array.isArray(days) ? days.find((day) => Number(day.day_of_week) === dow) : null;
    const isWorking = Boolean(row?.is_working_day);
    const minutes = Number(row?.operating_minutes || 0);
    return {
      day_of_week: dow,
      is_working_day: isWorking,
      operating_minutes: isWorking && Number.isFinite(minutes) ? Math.max(0, Math.round(minutes)) : 0,
    };
  });
}

export function normalizeWorkPatternPayload(payload = {}) {
  return {
    name: cleanOptionalText(payload.name),
    description: cleanOptionalText(payload.description),
    base_daily_minutes: Number(payload.base_daily_minutes || 480),
    valid_from: payload.valid_from || null,
    valid_to: payload.valid_to || null,
    is_default: Boolean(payload.is_default),
    is_active: payload.is_active !== false,
    days: normalizeDays(payload.days),
  };
}

export async function listSanitaryWorkPatterns(params = {}) {
  try {
    const res = await apiClient.get("/sanitary/work-patterns", {
      withCredentials: true,
      params: buildParams(params),
    });
    return Array.isArray(res?.data) ? res.data : [];
  } catch (err) {
    throwApiError("GET /sanitary/work-patterns", err);
  }
}

export async function getSanitaryWorkPattern(patternId) {
  assertId("getSanitaryWorkPattern", patternId);
  try {
    const res = await apiClient.get(`/sanitary/work-patterns/${patternId}`, { withCredentials: true });
    return res?.data;
  } catch (err) {
    throwApiError(`GET /sanitary/work-patterns/${patternId}`, err);
  }
}

export async function createSanitaryWorkPattern(payload = {}) {
  try {
    const res = await apiClient.post("/sanitary/work-patterns", normalizeWorkPatternPayload(payload), {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });
    return res?.data;
  } catch (err) {
    throwApiError("POST /sanitary/work-patterns", err);
  }
}

export async function updateSanitaryWorkPattern(patternId, payload = {}) {
  assertId("updateSanitaryWorkPattern", patternId);
  try {
    const res = await apiClient.put(`/sanitary/work-patterns/${patternId}`, normalizeWorkPatternPayload(payload), {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });
    return res?.data;
  } catch (err) {
    throwApiError(`PUT /sanitary/work-patterns/${patternId}`, err);
  }
}

export async function setSanitaryWorkPatternActive(patternId, isActive) {
  assertId("setSanitaryWorkPatternActive", patternId);
  try {
    const res = await apiClient.patch(
      `/sanitary/work-patterns/${patternId}/active`,
      { is_active: Boolean(isActive) },
      { withCredentials: true, headers: { "Content-Type": "application/json" } }
    );
    return res?.data;
  } catch (err) {
    throwApiError(`PATCH /sanitary/work-patterns/${patternId}/active`, err);
  }
}

export default {
  listSanitaryWorkPatterns,
  getSanitaryWorkPattern,
  createSanitaryWorkPattern,
  updateSanitaryWorkPattern,
  setSanitaryWorkPatternActive,
};
